import { useEffect, useState } from 'react'
import { motion } from 'motion/react'
import { ChevronRight } from 'lucide-react'
import { useSessionStore } from '@/stores'
import { useUIStore } from '@stores/useUIStore'
import { EmptyState } from '@components/ui'
import { InputType, BillingCycleType } from '@constants'
import {
  sortItems,
  getShortHumanReadableDate,
  getShortHumanReadableUTCDate,
  getItemStatus,
  getItemStatusClass,
  getItemFulfillmentStatus,
  getItemFulfillmentStatusClass,
  showFulfillment,
  isRecurring,
  getItemImageUrl,
  getItemIntents,
  getBundleUnitPrice,
  getBundleQuantity,
  getPaymentStatus,
  getPaymentStatusClass,
  formatCurrency,
  setFallbackImageUrl,
  cn,
} from '@/utils'
import OrderTotalsInfo from '@/components/steps/OrderTotalsInfo'
import SelectItemIntents from '@/components/steps/SelectItemIntents'
import apiClient from '@/services/api'

interface SelectItemsProps {
  input?: any
  label?: string
  defaults?: any[]
  noback?: boolean
  noselect?: boolean
  multiple?: boolean
  itemSelectLabel?: string
  onSelect?: (items: any[], intent?: any) => void
  onSelectItemInput?: (data: { item: any; input: any }) => void
  onBack?: () => void
  onSelectionChange?: (selected: Record<number, boolean>) => void
}

const itemAnim = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] as const },
  }),
}

const SkeletonItemRow = () => (
  <div className="flex items-center gap-4 px-5 py-4">
    <div className="w-[100px] h-[100px] sm:w-[145px] sm:h-[145px] rounded-lg skeleton shrink-0" />
    <div className="flex-1 space-y-2.5">
      <div className="h-4 w-3/5 rounded skeleton" />
      <div className="h-3.5 w-1/3 rounded skeleton" />
      <div className="h-3.5 w-2/5 rounded skeleton" />
      <div className="h-3.5 w-1/4 rounded skeleton" />
    </div>
    <div className="h-11 w-32 rounded-lg skeleton hidden sm:block" />
  </div>
)

const OrderCardSkeleton = ({ index = 0, items = 1 }: { index?: number; items?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.12, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] as const }}>
    <div
      className="rounded-xl overflow-hidden mb-4"
      style={{
        border: '1px solid var(--border-color, #e5e7eb)',
        backgroundColor: 'var(--content-background-color)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
      <div
        className="flex items-center gap-3 px-5 py-3"
        style={{
          borderBottom: '1px solid var(--border-color, #e5e7eb)',
          backgroundColor: 'color-mix(in srgb, var(--theme-color) 3%, var(--content-background-color))',
        }}>
        <div className="h-4 w-24 rounded skeleton" />
        <div className="h-4 w-20 rounded skeleton" />
        <div className="h-5 w-12 rounded-full skeleton" />
      </div>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} style={{ borderBottom: i < items - 1 ? '1px solid var(--border-color, #e5e7eb)' : undefined }}>
          <SkeletonItemRow />
        </div>
      ))}
    </div>
    <div className="flex justify-between items-center max-w-[400px] ml-auto mb-6 -mt-1">
      <div className="flex items-center gap-2">
        <div className="h-3.5 w-36 rounded skeleton" />
        <div className="h-3.5 w-3.5 rounded skeleton" />
      </div>
      <div className="h-4 w-16 rounded skeleton" />
    </div>
  </motion.div>
)

const StatusBadge = ({ text, className = '' }: { text: string; className?: string }) => (
  <span className={`order-badge ${className}`}>{text}</span>
)

const SelectItems = ({
  input,
  label = 'Continue',
  defaults = [],
  noback = false,
  noselect = false,
  multiple = false,
  itemSelectLabel = 'Select',
  onSelect,
  onSelectItemInput,
  onBack,
  onSelectionChange,
}: SelectItemsProps) => {
  const session = useSessionStore(s => s.session)
  const campaign = useSessionStore(s => s.campaign)
  const ready = useUIStore(s => s.ready)
  const subscriptionStatusLabels = useSessionStore(s => s.subscriptionStatusLabels)()
  const subscriptionOnlyStore = useSessionStore(s => s.subscriptionOnly)
  const setSubscriptionOnlyStore = useSessionStore(s => s.setSubscriptionOnly)

  const [orders, setOrders] = useState<any[]>([])
  const [selectedItemMap, setSelectedItemMap] = useState<Record<number, boolean>>({})
  const [canContinue, setCanContinue] = useState(false)
  const [loading, setLocalLoading] = useState(true)
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null)
  const [isSubscriptionOnly, setIsSubscriptionOnly] = useState(subscriptionOnlyStore)
  const [isItemStatus, setIsItemStatus] = useState(false)

  const activeItemRequired = input?.requires_active_item
  const isCheckout = input?.type === InputType.CheckoutConfirm
  const useIntentButtons = !multiple && !!(session as any)?.use_intents
  const additionalInputs: any[] = (session as any)?.additional_inputs || []
  const allowedIntents: number[] = (() => {
    if (!session) {
      return []
    }
    if (useIntentButtons) {
      return (session as any).intents || []
    }
    return additionalInputs.map((i: any) => i.type)
  })()

  const updateStatus = (map: Record<number, boolean>) => {
    setCanContinue(Object.keys(map).length !== 0)
    onSelectionChange?.(map)
  }

  const fetchItems = async (subOnly: boolean, subId: number | null, itemStat: boolean) => {
    setLocalLoading(true)
    const newMap: Record<number, boolean> = {}

    if (multiple) {
      defaults.forEach(item => {
        newMap[item.id] = true
      })
    }

    const sessionIntents = new Set(allowedIntents)
    const settings = (campaign as any)?.portal_settings || {}

    let params: Record<string, any> = { is_active: activeItemRequired, checkout: isCheckout }
    if (subOnly) {
      params = { latest_cycle: true, billing_cycle_type: BillingCycleType.Recurring }
      if (itemStat && (session as any)?.selected_item?.subscription_id) {
        params.subscription_id = (session as any).selected_item.subscription_id
      }
    } else if (subId) {
      params = { subscription_id: subId }
    }

    try {
      const result = await apiClient.get('/order_items/', { params })
      const items: any[] = result.data.results || result.data || []
      let lastOrderId: number | null = null
      const newOrders: any[] = []
      let order: any = {}
      let startIndex = 0

      sortItems(items)

      for (let i = 0; i < items.length; i++) {
        const orderId = items[i].order.id
        items[i].actions = getItemIntents(items[i], settings).filter((intent: number) => sessionIntents.has(intent))

        if (orderId !== lastOrderId) {
          if (i > 0) {
            order.items = items.slice(startIndex, i)
            newOrders.push(order)
          }
          lastOrderId = orderId
          startIndex = i
          order = { ...items[i].order }
        }

        if (i === items.length - 1) {
          order.items = items.slice(startIndex)
          newOrders.push(order)
        }
      }

      setOrders(newOrders)
      setSelectedItemMap(newMap)
      updateStatus(newMap)
      setLocalLoading(false)
      if (!items.length) {
        setCanContinue(true)
      }
      ready()
    } catch {
      ready()
      setLocalLoading(false)
    }
  }

  useEffect(() => {
    let subOnly = false
    let itemStat = false
    if (input) {
      subOnly = input.type === InputType.SubscriptionItem
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Local flag and store flag must move together.
      setIsSubscriptionOnly(subOnly)
      setSubscriptionOnlyStore(subOnly)
    } else {
      subOnly = !!(session as any)?.subscription_only
      setIsSubscriptionOnly(subOnly)
      setSubscriptionOnlyStore(subOnly)
      itemStat = true
      setIsItemStatus(true)
    }
    fetchItems(subOnly, null, itemStat)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only; fetchItems is re-created each render and would re-request.
  }, [])

  const done = () => {
    if (!canContinue) {
      return
    }
    const selectedItems: any[] = []
    orders.forEach(order => {
      order.items?.forEach((item: any) => {
        if (item.id in selectedItemMap) {
          selectedItems.push(item)
        }
      })
    })
    onSelect?.(selectedItems)
  }

  const goBack = () => {
    if (selectedSubscriptionId) {
      setSelectedSubscriptionId(null)
      setIsSubscriptionOnly(true)
      fetchItems(true, null, isItemStatus)
    } else {
      onBack?.()
    }
  }

  const selectItemButton = (item: any, intent?: any) => {
    if (multiple) {
      const newMap = { ...selectedItemMap }
      if (item.id in newMap) {
        delete newMap[item.id]
      } else {
        newMap[item.id] = true
      }
      setSelectedItemMap(newMap)
      updateStatus(newMap)
    } else if (useIntentButtons) {
      onSelect?.([item], intent)
    } else if (intent) {
      const inp = additionalInputs.find((i: any) => i.type === intent)
      if (inp) {
        onSelectItemInput?.({ item, input: inp })
      }
    } else {
      onSelect?.([item])
    }
  }

  const isSelected = (item: any) => item.id in selectedItemMap
  const getName = (item: any) =>
    isSubscriptionOnly && item.next_cycle_product ? item.next_cycle_product.name : item.name
  const getVariant = (item: any) => (isSubscriptionOnly && item.next_variant ? item.next_variant : item.variant)
  const getPrice = (item: any) => (isSubscriptionOnly ? item.recurring_price : item.price)
  const getOriginalPrice = (item: any) => (isSubscriptionOnly ? item.next_recurring_price : item.price)
  const isDiscounted = (item: any) =>
    isSubscriptionOnly && parseFloat(getOriginalPrice(item)) - parseFloat(getPrice(item)) > Number.EPSILON
  const isSubscriptionActive = (item: any) =>
    item.billing_cycle_type === BillingCycleType.Recurring && item.can_cancel_subscription
  const showItemFulfillment = (item: any) => !isSubscriptionOnly && showFulfillment(item)

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, item: any) => {
    setFallbackImageUrl(e.nativeEvent, item, 'medium', true, isSubscriptionOnly)
  }

  if (loading) {
    return (
      <section className="pt-4 px-3 max-w-4xl mx-auto">
        <OrderCardSkeleton index={0} />
        <OrderCardSkeleton index={1} items={2} />
        <OrderCardSkeleton index={2} />
      </section>
    )
  }

  if (!orders.length) {
    return (
      <section className="pt-4 px-3 max-w-4xl mx-auto">
        <EmptyState title="No orders found" description="We couldn’t find any orders linked to your account." />
      </section>
    )
  }

  return (
    <section className="pt-4 px-3 max-w-4xl mx-auto">
      {orders.map((order, orderIndex) => (
        <div key={order.id}>
          <motion.div
            className="rounded-xl overflow-hidden mb-4"
            style={{
              border: '1px solid var(--border-color, #e5e7eb)',
              backgroundColor: 'var(--content-background-color)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: orderIndex * 0.1, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] as const }}>
            {/* Order header */}
            {!isSubscriptionOnly && (
              <div
                className="flex items-center gap-2 flex-wrap px-5 py-3 text-fluid-base"
                style={{
                  borderBottom: '1px solid var(--border-color, #e5e7eb)',
                  backgroundColor: 'color-mix(in srgb, var(--theme-color) 3%, var(--content-background-color))',
                  color: 'var(--text-color)',
                }}>
                <span className="font-semibold" style={{ color: 'var(--text-color)' }}>
                  #{order.display_order_id || order.order_id}
                </span>
                <span style={{ color: 'var(--border-color)' }}>|</span>
                <span>{getShortHumanReadableDate(order.created)}</span>
                <span style={{ color: 'var(--border-color)' }}>|</span>
                <StatusBadge text={getPaymentStatus(order)} className={getPaymentStatusClass(order)} />
              </div>
            )}

            {/* Items */}
            {order.items?.map((item: any, itemIndex: number) => (
              <motion.div
                key={item.id}
                custom={itemIndex}
                variants={itemAnim}
                initial="hidden"
                animate="visible"
                className={isSelected(item) ? 'ring-2 ring-inset' : ''}
                style={{
                  borderBottom:
                    itemIndex < order.items.length - 1 ? '1px solid var(--border-color, #e5e7eb)' : undefined,
                  ...(isSelected(item)
                    ? {
                        ringColor: 'var(--theme-color)',
                        backgroundColor: 'color-mix(in srgb, var(--theme-color) 4%, var(--content-background-color))',
                      }
                    : {}),
                }}>
                <div className="flex items-center gap-4 px-5 py-4">
                  {/* Product image */}
                  <div
                    className="w-[100px] h-[100px] sm:w-[145px] sm:h-[145px] shrink-0 rounded-lg overflow-hidden flex items-center justify-center"
                    style={{
                      border: '1px solid var(--border-color, #e5e7eb)',
                      backgroundColor: 'var(--content-background-color)',
                    }}>
                    <img
                      src={getItemImageUrl(item, 'medium', isSubscriptionOnly)}
                      alt=""
                      className="max-w-full max-h-full object-contain"
                      onError={e => handleImageError(e, item)}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-semibold text-fluid-lg leading-snug mb-1"
                      style={{ color: 'var(--text-color)' }}
                      dangerouslySetInnerHTML={{ __html: getName(item) }}
                    />

                    {getVariant(item) && (
                      <div className="text-fluid-base mt-0.5" style={{ color: 'var(--light-text-color)' }}>
                        {getVariant(item).options?.map(
                          (opt: any) =>
                            !opt.is_deleted && (
                              <span key={opt.id} className="mr-2">
                                {opt.name}: {opt.value}
                              </span>
                            )
                        )}
                      </div>
                    )}

                    <p className="text-fluid-base mt-0.5 mb-0" style={{ color: 'var(--light-text-color)' }}>
                      {isDiscounted(item) && (
                        <span className="line-through opacity-60 mr-1">
                          {formatCurrency(getOriginalPrice(item), order.currency)}
                        </span>
                      )}
                      {formatCurrency(getPrice(item), order.currency)}
                      {getBundleQuantity(item, isSubscriptionOnly) > 1 && (
                        <span>
                          {' '}
                          ({getBundleQuantity(item, isSubscriptionOnly)} x{' '}
                          {formatCurrency(getBundleUnitPrice(item, isSubscriptionOnly), order.currency)})
                        </span>
                      )}
                    </p>

                    {showItemFulfillment(item) && (
                      <p
                        className="flex items-center gap-1.5 text-fluid-base mt-0.5 mb-0"
                        style={{ color: 'var(--light-text-color)' }}>
                        <span>Shipment</span>
                        <StatusBadge
                          text={getItemFulfillmentStatus(item)}
                          className={getItemFulfillmentStatusClass(item)}
                        />
                      </p>
                    )}

                    <p
                      className="flex items-center gap-1.5 text-fluid-base mt-0.5 mb-0"
                      style={{ color: 'var(--light-text-color)' }}>
                      {isRecurring(item) ? (
                        <>
                          <span>Subscription</span>
                          <StatusBadge
                            text={getItemStatus(item, subscriptionStatusLabels, false)}
                            className={getItemStatusClass(item)}
                          />
                        </>
                      ) : (
                        <span>One-time purchase</span>
                      )}
                    </p>

                    {isSubscriptionActive(item) &&
                      item.next_bill_date &&
                      item.campaign_product?.show_next_bill_price && (
                        <p className="text-fluid-base mt-0.5 mb-0" style={{ color: 'var(--light-text-color)' }}>
                          Next {item.is_shippable ? 'shipment' : 'billing'}:{' '}
                          <span className="font-semibold">{getShortHumanReadableUTCDate(item.next_bill_date)}</span>
                        </p>
                      )}

                    {!isSubscriptionOnly && (item.next_cycle_product || item.next_variant) && (
                      <div
                        className="flex items-start gap-2 text-fluid-base mt-1 mb-0"
                        style={{ color: 'var(--light-text-color)' }}>
                        <span className="shrink-0">Next cycle:</span>
                        {item.next_cycle_product && (
                          <img
                            src={getItemImageUrl(item, 'small', true)}
                            alt=""
                            className="w-8 h-8 rounded object-contain shrink-0"
                            style={{ border: '1px solid var(--border-color, #e5e7eb)' }}
                            onError={e => setFallbackImageUrl(e.nativeEvent, item, 'small', true, true)}
                          />
                        )}
                        <div className="flex flex-col min-w-0">
                          {item.next_cycle_product && (
                            <span className="font-medium" style={{ color: 'var(--text-color)' }}>
                              {item.next_cycle_product.name}
                            </span>
                          )}
                          {item.next_variant && (
                            <span>
                              {item.next_variant.options?.map(
                                (opt: any) =>
                                  !opt.is_deleted && (
                                    <span key={opt.id} className="mr-2">
                                      {opt.name}: {opt.value}
                                    </span>
                                  )
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Desktop action button */}
                  {!noselect && (
                    <div className="hidden md:flex shrink-0">
                      {useIntentButtons && item.actions?.length > 0 ? (
                        <div className="w-[230px]" onClick={e => e.stopPropagation()}>
                          <SelectItemIntents
                            item={item}
                            intents={item.actions}
                            onSelectIntent={intent => selectItemButton(item, intent)}
                          />
                        </div>
                      ) : (
                        <motion.button
                          type="button"
                          className="flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-sm font-semibold uppercase tracking-wide no-transform"
                          style={{
                            backgroundColor: 'var(--theme-color)',
                            color: 'var(--button-text-color, #fff)',
                          }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={e => {
                            e.stopPropagation()
                            selectItemButton(item)
                          }}>
                          {isSelected(item) ? 'Selected' : itemSelectLabel}
                          {!isSelected(item) && <ChevronRight size={16} />}
                        </motion.button>
                      )}
                    </div>
                  )}
                </div>

                {/* Mobile action button */}
                {!noselect && (
                  <div className="md:hidden px-5 pb-4" onClick={e => e.stopPropagation()}>
                    {useIntentButtons && item.actions?.length > 0 ? (
                      <SelectItemIntents
                        item={item}
                        intents={item.actions}
                        onSelectIntent={intent => selectItemButton(item, intent)}
                      />
                    ) : (
                      <motion.button
                        type="button"
                        className="flex items-center justify-center gap-1.5 rounded-lg px-5 py-2.5 text-sm font-semibold uppercase tracking-wide w-full no-transform"
                        style={{
                          backgroundColor: 'var(--theme-color)',
                          color: 'var(--button-text-color, #fff)',
                        }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => selectItemButton(item)}>
                        {isSelected(item) ? 'Selected' : itemSelectLabel}
                        {!isSelected(item) && <ChevronRight size={16} />}
                      </motion.button>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>

          {!isSubscriptionOnly && <OrderTotalsInfo order={order} />}
        </div>
      ))}

      {/* Bottom buttons — when paired, both share equal width (280px); back alone uses
          the standard back-btn sizing (250px on mobile, 280px on lg+). */}
      <div className="flex flex-wrap justify-center items-center gap-3 mb-6 mt-2 md:max-w-[240px] md:mx-auto">
        {(session?.can_undo || (session as any)?.is_step_preview) && !noback && (
          <button
            type="button"
            onClick={goBack}
            className={cn('btn-base back-btn', multiple && '!w-[280px] !max-w-[40vw]')}>
            Back
          </button>
        )}
        {multiple && (
          <button
            type="button"
            onClick={done}
            disabled={!canContinue}
            className="btn-base input-choice-btn font-normal !w-[280px] !max-w-[40vw]">
            {label}
          </button>
        )}
      </div>
    </section>
  )
}

export default SelectItems
